/**
 * Live agent events over SSE (fetch streaming — EventSource can't send an Authorization header).
 *
 * - Resumes from the last event id it delivered (`?after=`), so a reconnect never replays or skips.
 * - Reconnects with backoff (1s → 15s), resetting after a healthy connection.
 * - Gets a fresh Supabase token for every connection.
 * - Tolerates `: keepalive` comments; ignores anything that isn't a well-formed protocol v1 event.
 * - Stall watchdog: the server sends a keepalive every 15s, so no bytes for `stallMs` (35s) means the socket is
 *   half-open (typical after the phone slept); the connection is dropped and reopened at once.
 * - A 401 refreshes the session and retries (a token that expired while the app was in the background); it only
 *   closes when there is no session to refresh. 403 / 404 still close: not enabled, or not this creator's session.
 * - App-wide control (src/native/platform.ts wires these): `reconnectAgentStreams()` reopens every live stream now
 *   (app resume, network back); `setAgentStreamsOnline(false)` holds the reconnect backoff until the network
 *   returns instead of spinning through attempts offline.
 */
import type { AnyAgentEvent } from './protocol/types'

export type StreamStatus = 'connecting' | 'open' | 'reconnecting' | 'closed'

export interface StreamOptions {
    sessionId?: string
    /** Full path override (e.g. job events); defaults to the session stream. */
    path?: string
    after?: number
    onEvent: (e: AnyAgentEvent) => void
    onStatus?: (s: StreamStatus) => void
    signal: AbortSignal
    /** Stop after this run's run.finished (CLI / tests). */
    untilRunFinished?: string
    baseUrl?: string
    getToken?: () => Promise<string | null>
    /**
     * After a 401: refresh the session. Resolve the new token, `null` when signed out (the stream closes), or
     * throw when the refresh itself could not reach the server (the stream keeps retrying).
     */
    refreshToken?: () => Promise<string | null>
    fetchImpl?: typeof fetch
    backoffMs?: (attempt: number) => number
    /** No bytes (not even a keepalive) for this long while connected: reconnect. Default 35s. */
    stallMs?: number
}

/** The server's keepalive is 15s; two missed plus slack. */
export const STREAM_STALL_MS = 35_000
/** 401s in a row, each after a successful token refresh, before the stream gives up. */
const MAX_AUTH_RETRIES = 4

/** Split an SSE buffer into complete event `data` payloads; returns [payloads, rest]. */
export function parseSSE(buffer: string): [string[], string] {
    const out: string[] = []
    let rest = buffer.replace(/\r\n/g, '\n')
    let i: number
    while ((i = rest.indexOf('\n\n')) >= 0) {
        const block = rest.slice(0, i)
        rest = rest.slice(i + 2)
        const data = block
            .split('\n')
            .filter((l) => l.startsWith('data:'))
            .map((l) => l.slice(5).replace(/^ /, ''))
            .join('\n')
        if (data) out.push(data)
    }
    return [out, rest]
}

async function defaultToken(): Promise<string | null> {
    try {
        const { supabase } = await import('@/supabase')
        return (await supabase.auth.getSession())?.data?.session?.access_token ?? null
    } catch {
        return null
    }
}

async function defaultRefresh(): Promise<string | null> {
    const { supabase } = await import('@/supabase')
    const { isAuthRetryableFetchError } = await import('@supabase/supabase-js')
    const { data, error } = await supabase.auth.refreshSession()
    // The auth server was unreachable (the network isn't back yet): keep trying rather than close.
    if (error && isAuthRetryableFetchError(error)) throw error
    return data?.session?.access_token ?? null
}

/**
 * Client-only sessions (the product tour's example conversation, guest mode) serve their events from memory.
 * A source claims session ids; `streamEvents` then reads from it instead of the network.
 */
export type LocalStream = (o: { sessionId: string; after: number; onEvent: (e: AnyAgentEvent) => void; signal: AbortSignal }) => void
const localStreams: { claims: (sessionId: string) => boolean; run: LocalStream }[] = []
export function registerLocalStream(claims: (sessionId: string) => boolean, run: LocalStream): () => void {
    const entry = { claims, run }
    localStreams.push(entry)
    return () => {
        const i = localStreams.indexOf(entry)
        if (i >= 0) localStreams.splice(i, 1)
    }
}

const defaultBackoff = (attempt: number) => Math.min(15_000, 1000 * 2 ** Math.max(0, attempt - 1))

// ── app-wide control ───────────────────────────────────────────────────────────────────────────────────
interface LiveStream {
    /** Drop the current connection (or skip the backoff wait) and connect again now. */
    reconnectNow(): void
    /** Wake a stream that is holding its backoff for the network. */
    wake(): void
}
const live = new Set<LiveStream>()
let networkOnline = true

/** Reopen every live stream now (app resumed, network changed). Returns how many were told. */
export function reconnectAgentStreams(): number {
    for (const s of live) s.reconnectNow()
    return live.size
}

/** While offline, reconnect attempts wait for the network instead of backing off; going online wakes them. */
export function setAgentStreamsOnline(online: boolean): void {
    if (networkOnline === online) return
    networkOnline = online
    if (online) for (const s of live) s.wake()
}

/** Number of network streams currently running (tests, diagnostics). */
export function liveAgentStreamCount(): number {
    return live.size
}

class ConnectionAborted extends Error {}

/** Runs until `signal` aborts (or `untilRunFinished` is seen). Resolves with the last delivered event id. */
export async function streamEvents(opts: StreamOptions): Promise<number> {
    const local = opts.sessionId && !opts.path ? localStreams.find((l) => l.claims(opts.sessionId!)) : undefined
    if (local) {
        let last = opts.after ?? 0
        opts.onStatus?.('open')
        local.run({ sessionId: opts.sessionId!, after: last, signal: opts.signal,
                    onEvent: (e) => { last = e.id; opts.onEvent(e) } })
        await new Promise<void>((r) => opts.signal.addEventListener('abort', () => r(), { once: true }))
        opts.onStatus?.('closed')
        return last
    }
    const fetchImpl = opts.fetchImpl ?? fetch
    const base = (opts.baseUrl ?? (import.meta.env.VITE_APP_BACKEND_URL as string) ?? '').replace(/\/$/, '')
    const getToken = opts.getToken ?? defaultToken
    const refreshToken = opts.refreshToken ?? defaultRefresh
    const backoff = opts.backoffMs ?? defaultBackoff
    const stallMs = opts.stallMs ?? STREAM_STALL_MS
    const path = opts.path ?? `/agent/v2/sessions/${opts.sessionId}/events`
    let cursor = opts.after ?? 0
    let attempt = 0
    let authRetries = 0
    let first = true
    let done = false
    /** The current connection; aborting it drops the socket without stopping the stream. */
    let conn: AbortController | null = null
    /** Set while waiting between attempts; calling it ends the wait. */
    let endWait: (() => void) | null = null
    /** The next connection skips the backoff (a forced reconnect or a stall). */
    let immediate = false

    const handle: LiveStream = {
        reconnectNow() {
            immediate = true
            conn?.abort()
            endWait?.()
        },
        wake() {
            endWait?.()
        },
    }
    live.add(handle)

    try {
        while (!opts.signal.aborted && !done) {
            opts.onStatus?.(first ? 'connecting' : 'reconnecting')
            first = false
            const c = new AbortController()
            conn = c
            const onOuterAbort = () => c.abort()
            opts.signal.addEventListener('abort', onOuterAbort, { once: true })
            // Rejects when this connection is dropped, so a read that never returns (a fake or a stubborn body)
            // can't hold the loop.
            const dropped = new Promise<never>((_, reject) => {
                c.signal.addEventListener('abort', () => reject(new ConnectionAborted('connection dropped')), { once: true })
            })
            dropped.catch(() => {})
            let watchdog: ReturnType<typeof setTimeout> | undefined
            const arm = () => {
                clearTimeout(watchdog)
                watchdog = setTimeout(() => {
                    immediate = true
                    c.abort()
                }, stallMs)
            }
            try {
                // The watchdog covers the connect too: a request on a dead socket can hang as long as a read.
                arm()
                const token = await Promise.race([getToken(), dropped])
                const qs = new URLSearchParams({ after: String(cursor) })
                if (opts.untilRunFinished) qs.set('until_run_finished', opts.untilRunFinished)
                const res = await Promise.race([
                    fetchImpl(`${base}${path}?${qs}`, {
                        headers: { Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        signal: c.signal,
                    }),
                    dropped,
                ])
                if (res.status === 401) {
                    clearTimeout(watchdog)
                    // The token expired (often while the app slept). Refresh and try again; only a missing session,
                    // or 401s that survive fresh tokens, close the stream.
                    let fresh: string | null | undefined
                    try {
                        fresh = await refreshToken()
                    } catch {
                        fresh = undefined // the refresh couldn't reach the server: retry later
                    }
                    if (fresh === null || (typeof fresh === 'string' && ++authRetries > MAX_AUTH_RETRIES)) {
                        break
                    }
                    // A fresh token the first time: go again at once. Otherwise (refresh unreachable, or a repeat
                    // 401) take the backoff, so a flaky auth server is never hammered.
                    if (typeof fresh === 'string' && authRetries === 1) immediate = true
                    throw new Error('stream 401')
                }
                if (res.status === 403 || res.status === 404) {
                    // Not retryable: not enabled, or not this creator's session.
                    break
                }
                if (!res.ok || !res.body) throw new Error(`stream ${res.status}`)
                opts.onStatus?.('open')
                arm()
                const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
                c.signal.addEventListener('abort', () => { reader.cancel().catch(() => {}) }, { once: true })
                let buf = ''
                for (;;) {
                    const { value, done: eof } = await Promise.race([reader.read(), dropped])
                    if (eof) break
                    arm()
                    attempt = 0
                    authRetries = 0
                    const [payloads, rest] = parseSSE(buf + value)
                    buf = rest
                    for (const p of payloads) {
                        let ev: AnyAgentEvent
                        try {
                            ev = JSON.parse(p)
                        } catch {
                            continue
                        }
                        if (typeof ev?.id !== 'number' || ev.protocol_version !== 1 || ev.id <= cursor) continue
                        cursor = ev.id
                        opts.onEvent(ev)
                        if (opts.untilRunFinished && ev.type === 'run.finished' && ev.run_id === opts.untilRunFinished) {
                            done = true
                        }
                    }
                    if (done) break
                }
                if (opts.untilRunFinished && done) break
            } catch {
                if (opts.signal.aborted) break
            } finally {
                clearTimeout(watchdog)
                opts.signal.removeEventListener('abort', onOuterAbort)
                c.abort()
                if (conn === c) conn = null
            }
            if (opts.signal.aborted || done) break
            if (immediate) {
                // Resume, network change or a stall: the server is fine, the socket wasn't. Go again now.
                immediate = false
                attempt = 0
                continue
            }
            attempt += 1
            await waitBeforeRetry(backoff(attempt))
        }
    } finally {
        live.delete(handle)
        conn = null
    }
    opts.onStatus?.('closed')
    return cursor

    /** Back off, or (offline) hold until the network returns; a forced reconnect or abort ends the wait early. */
    function waitBeforeRetry(ms: number): Promise<void> {
        return new Promise<void>((resolve) => {
            let t: ReturnType<typeof setTimeout> | undefined
            const finish = () => {
                clearTimeout(t)
                opts.signal.removeEventListener('abort', finish)
                endWait = null
                resolve()
            }
            endWait = finish
            opts.signal.addEventListener('abort', finish, { once: true })
            if (networkOnline) t = setTimeout(finish, ms)
            // Offline: no timer. `setAgentStreamsOnline(true)` (wake) or `reconnectAgentStreams()` ends the wait.
        })
    }
}
